import {CatalogProductCategoryAPIApi} from "@/api/pharma-cpc-product-category-mgmt";
import {RestService} from "@/services/rest/RestService";
import {CustomerContext} from "@/context/CustomerContext";
import {
    CustomerCategoryImageRestDto,
    CustomerProductCategoryOverviewResultsRestDto,
    CustomerProductCategoryRestDto,
    CodeResponseRestDto,
    MainProductCategoriesOverviewResultsRestDto,
    MasterMainProductCategoriesOverviewResultsRestDto,
    ProductCategoryCustomerNameListRestDto,
    ProductCategoryCustomerNameOverviewResultsRestDto, ProductCategoryCustomerNameRestDto,
    ProductCategoryCustomerSortWeightOverviewResultsRestDto,
    ProductCategoryCustomerSortWeightRestDto,
    StatusValueRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {AxiosError, AxiosResponse} from "axios";
import {CacheService} from "@/services/cache/CacheService";
import {CacheKeyEnum} from "@/services/cache/CacheKeyEnum";

export class ProductCategoryRestService extends RestService {

    private apiService: CatalogProductCategoryAPIApi;

    // TODO: caching

    protected constructor() {
        super();
        this.apiService = new CatalogProductCategoryAPIApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cpcUrl);
    }

    public async deleteProductCategoryCustomerSortWeight(id: string): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.deleteProductCategoryCustomerSortWeight(id, nonNullCustomerCode);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async deleteCustomerProductCategory(code: string): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.deleteCustomerProductCategory(code, nonNullCustomerCode);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async findCatalogMainProductCategories(language: string): Promise<MainProductCategoriesOverviewResultsRestDto> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        const result = await this.apiService.findCatalogMainProductCategories(nonNullCustomerCode, language);
        return result.data;
    }

    public async findCatalogMasterMainProductCategories(): Promise<MasterMainProductCategoriesOverviewResultsRestDto> {
        let promise: Promise<AxiosResponse<MasterMainProductCategoriesOverviewResultsRestDto>> =
            CacheService.getInstance().get(CacheKeyEnum.MASTER_MAIN_CATEGORIES);

        if (promise !== null) {
            const result: AxiosResponse<MasterMainProductCategoriesOverviewResultsRestDto> = await promise;
            return result.data;
        }

        promise = this.apiService.findCatalogMasterMainProductCategories();

        CacheService.getInstance().set(CacheKeyEnum.MASTER_MAIN_CATEGORIES, promise);

        const result: AxiosResponse<MasterMainProductCategoriesOverviewResultsRestDto> = await promise;
        return result.data;
    }

    public async updateStatus(
        status: StatusValueRestDto,
        categoryCode: string
    ): Promise<void> {
        await this.apiService.updateCategoryStatus(status, categoryCode);
    }

    public async findCustomerProductCategories(language: string): Promise<CustomerProductCategoryOverviewResultsRestDto> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        const result = await this.apiService.findCustomerProductCategories(nonNullCustomerCode, language);
        return result.data;
    }

    public async getCustomerProductCategory(code: string): Promise<CustomerProductCategoryRestDto> {
        const result = await this.apiService.getCustomerProductCategory(code);
        return result.data;
    }

    public async getProductCategoryCustomerName(code: string): Promise<ProductCategoryCustomerNameRestDto | null> {
        try {
            const result = await this.apiService.getProductCategoryCustomerName(code);
            return result.data;
        } catch (e: any) {
            if (e instanceof AxiosError && e.response?.status === 404) {
                // Handle 404 case specifically
                return null;
            }
            // Re-throw all other errors
            throw e;
        }
    }

    public async getProductCategoryCustomerImage(code: string): Promise<CustomerCategoryImageRestDto | null> {
        try {
            const result = await this.apiService.getProductCategoryCustomerImage(code);
            return result.data;
        } catch (e: any) {
            if (e instanceof AxiosError && e.response?.status === 404) {
                // Handle 404 case specifically
                return null;
            }
            // Re-throw all other errors
            throw e;
        }
    }

    public async findProductCategoryCustomerNames(): Promise<ProductCategoryCustomerNameOverviewResultsRestDto> {
        const result = await this.apiService.findProductCategoryCustomerNames();
        return result.data;
    }

    public async findProductCategoryCustomerSortWeights(): Promise<ProductCategoryCustomerSortWeightOverviewResultsRestDto> {
        const result = await this.apiService.findProductCategoryCustomerSortWeights();
        return result.data;
    }

    public async updateCustomerProductCategory(
        body: CustomerProductCategoryRestDto,
        code: string
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.updateCustomerProductCategory(body, code);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async updateProductCategoryCustomerName(
        body: ProductCategoryCustomerNameRestDto,
        code: string
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.updateProductCategoryCustomerName(body, code);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async updateProductCategoryCustomerImage(
        body: CustomerCategoryImageRestDto,
        code: string
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.updateProductCategoryCustomerImage(body, code);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async updateProductCategoryCustomerNames(
        body: ProductCategoryCustomerNameListRestDto
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.updateProductCategoryCustomerNames(body);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async createProductCategoryCustomerSortWeight(
        body: ProductCategoryCustomerSortWeightRestDto,
        code: string
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.createProductCategoryCustomerSortWeight(body, code);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async updateProductCategoryCustomerSortWeight(
        body: ProductCategoryCustomerSortWeightRestDto,
        code: string
    ): Promise<void> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        await this.apiService.updateProductCategoryCustomerSortWeight(body, code);
        this.clearCategoryCache(nonNullCustomerCode);
    }

    public async addCustomerProductCategory(
        body: CustomerProductCategoryRestDto
    ): Promise<string> {
        const nonNullCustomerCode: string = CustomerContext.getCustomerCode();
        const response: AxiosResponse<CodeResponseRestDto> = await this.apiService.addCustomerProductCategory(body);
        this.clearCategoryCache(nonNullCustomerCode);
        return response.data.code;
    }

    public clearCategoryCache(customerCode: string): void {
        const nonNullCustomerCode: string = customerCode ?? CustomerContext.getCustomerCode();
        CacheService.getInstance().invalidateByPrefix('PRODUCT_CATEGORIES', nonNullCustomerCode);
    }
}
